import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Security as SecurityIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import TableActionButton from "../../components/layout/TableActionButton";
import { useRBAC } from "../../hooks/useRBAC";
import {
  permissionAPI,
  roleAPI,
  type CreateRolePayload,
  type ModulePermissionRow,
  type RoleRow,
  type UpdateRolePayload,
} from "../../services/roleAPI";
import { tokens } from "../../theme";

const SYSTEM_KEY = "SUPER_ADMIN";

/** Extra actions allowed per module — only show relevant extras in the matrix. */
const EXTRAS_BY_MODULE: Record<string, { value: string; label: string }[]> = {
  sales: [
    { value: "refund", label: "Refund" },
    { value: "random_sale", label: "Random sale" },
  ],
  nfc_cards: [
    { value: "block_card", label: "Block card" },
    { value: "unblock_card", label: "Unblock card" },
    { value: "assign_card", label: "Assign card" },
  ],
  cardholders: [{ value: "set_primary", label: "Set primary card" }],
  recharges: [
    { value: "direct_recharge", label: "Direct recharge" },
    { value: "approve_recharge", label: "Approve recharge" },
  ],
  inventory: [{ value: "adjust_stock", label: "Adjust stock" }],
  permissions: [{ value: "manage_rbac", label: "Manage RBAC" }],
};

const extrasForModule = (moduleKey: string) =>
  EXTRAS_BY_MODULE[moduleKey] || [];

const emptyCreate: CreateRolePayload = {
  key: "",
  name: "",
  description: "",
  isSystem: false,
  isActive: true,
  copyFromRole: "",
};

const RolesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { canRead, canAssignFeaturePermissions, hasExtraAction } = useRBAC();
  const canManage = canAssignFeaturePermissions() || hasExtraAction("permissions", "manage_rbac");

  const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(
    null
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateRolePayload>(emptyCreate);
  const [editRole, setEditRole] = useState<RoleRow | null>(null);
  const [editForm, setEditForm] = useState<UpdateRolePayload>({});
  const [deleteRole, setDeleteRole] = useState<RoleRow | null>(null);
  const [permRole, setPermRole] = useState<RoleRow | null>(null);

  const { data: roles = [], isLoading, error } = useQuery({
    queryKey: ["roles"],
    queryFn: () => roleAPI.list(),
    enabled: canRead("permissions"),
  });

  const { data: rolePerms = [], isLoading: permsLoading } = useQuery({
    queryKey: ["role-permissions", permRole?.key],
    queryFn: () => permissionAPI.listByRole(permRole!.key),
    enabled: Boolean(permRole?.key),
  });

  const createMutation = useMutation({
    mutationFn: roleAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      setCreateOpen(false);
      setCreateForm(emptyCreate);
      setAlert({ type: "success", message: "Role created" });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err?.response?.data?.message || err?.message || "Create failed",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateRolePayload }) =>
      roleAPI.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      setEditRole(null);
      setAlert({ type: "success", message: "Role updated" });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err?.response?.data?.message || err?.message || "Update failed",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => roleAPI.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      setDeleteRole(null);
      setAlert({ type: "success", message: "Role deleted" });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err?.response?.data?.message || err?.message || "Delete failed",
      });
    },
  });

  const seedMutation = useMutation({
    mutationFn: permissionAPI.seed,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role-permissions"] });
      setAlert({ type: "success", message: "RBAC matrix re-seeded" });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err?.response?.data?.message || "Seed failed",
      });
    },
  });

  const updatePermMutation = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<ModulePermissionRow>;
    }) => permissionAPI.update(id, body as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["role-permissions", permRole?.key] });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err?.response?.data?.message || "Permission update failed",
      });
    },
  });

  const copyFromOptions = useMemo(
    () => roles.map((r) => r.key),
    [roles]
  );

  const openEdit = (role: RoleRow) => {
    if (role.key === SYSTEM_KEY) {
      setAlert({ type: "error", message: "SUPER_ADMIN cannot be edited" });
      return;
    }
    setEditRole(role);
    setEditForm({
      name: role.name,
      description: role.description || "",
      isActive: role.isActive,
      sortOrder: role.sortOrder,
      isSystem: role.isSystem,
    });
  };

  const moduleLabel = (row: ModulePermissionRow) => {
    if (typeof row.module === "string") return (row as any).moduleName || row.module;
    return row.module?.name || row.module?.key || "";
  };

  const moduleKey = (row: ModulePermissionRow) => {
    if (typeof row.module === "string") return row.module;
    return row.module?.key || "";
  };

  if (!canRead("permissions")) {
    return (
      <Alert severity="warning">You do not have permission to view roles.</Alert>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Roles & permissions"
        subtitle="Dynamic roles. SUPER_ADMIN is a locked system role."
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Roles" },
        ]}
        action={
          <Stack direction="row" spacing={1}>
            {canManage && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={() => seedMutation.mutate()}
                disabled={seedMutation.isPending}
              >
                Seed matrix
              </Button>
            )}
            {canManage && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                Add role
              </Button>
            )}
          </Stack>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load roles
        </Alert>
      )}

      <DataListCard>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Sort</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5}>Loading…</TableCell>
                </TableRow>
              )}
              {!isLoading &&
                roles.map((role) => (
                  <TableRow key={role.id} hover>
                    <TableCell>
                      <Typography fontWeight={600} fontFamily="monospace" fontSize="0.85rem">
                        {role.key}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={500}>{role.name}</Typography>
                      {role.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {role.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {role.isSystem && (
                          <Chip
                            size="small"
                            label="System"
                            sx={{ bgcolor: alpha(tokens.colors.error.main, 0.12) }}
                          />
                        )}
                        <Chip
                          size="small"
                          label={role.isActive ? "Active" : "Inactive"}
                          color={role.isActive ? "success" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>{role.sortOrder}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Module permissions">
                          <IconButton size="small" onClick={() => setPermRole(role)}>
                            <SecurityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canManage && role.key !== SYSTEM_KEY && (
                          <TableActionButton
                            title="Edit"
                            onClick={() => openEdit(role)}
                          >
                            <EditIcon fontSize="small" />
                          </TableActionButton>
                        )}
                        {canManage && !role.isSystem && role.key !== SYSTEM_KEY && (
                          <TableActionButton
                            title="Delete"
                            onClick={() => setDeleteRole(role)}
                            variant="danger"
                          >
                            <DeleteIcon fontSize="small" />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataListCard>

      {/* Create */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Key"
              helperText="Uppercase identifier, e.g. BRANCH_MANAGER"
              value={createForm.key}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, key: e.target.value.toUpperCase() }))
              }
              fullWidth
              required
            />
            <TextField
              label="Name"
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={createForm.description || ""}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, description: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Copy permissions from</InputLabel>
              <Select
                label="Copy permissions from"
                value={createForm.copyFromRole || ""}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, copyFromRole: e.target.value }))
                }
              >
                <MenuItem value="">None (empty)</MenuItem>
                {copyFromOptions.map((k) => (
                  <MenuItem key={k} value={k}>
                    {k}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(createForm.isSystem)}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, isSystem: e.target.checked }))
                  }
                />
              }
              label="System role (cannot be deleted)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!createForm.key.trim() || !createForm.name.trim() || createMutation.isPending}
            onClick={() =>
              createMutation.mutate({
                ...createForm,
                key: createForm.key.trim(),
                name: createForm.name.trim(),
                copyFromRole: createForm.copyFromRole || undefined,
              })
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog open={Boolean(editRole)} onClose={() => setEditRole(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit role — {editRole?.key}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={editForm.name || ""}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={editForm.description || ""}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Sort order"
              type="number"
              value={editForm.sortOrder ?? 0}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))
              }
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive !== false}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, isActive: e.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRole(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={updateMutation.isPending}
            onClick={() =>
              editRole && updateMutation.mutate({ id: editRole.id, body: editForm })
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={Boolean(deleteRole)} onClose={() => setDeleteRole(null)}>
        <DialogTitle>Delete role?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteRole?.key}</strong>? Users must be reassigned first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRole(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() => deleteRole && deleteMutation.mutate(deleteRole.id)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions matrix */}
      <Dialog
        open={Boolean(permRole)}
        onClose={() => setPermRole(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Permissions — {permRole?.key}
          {permRole?.key === SYSTEM_KEY && (
            <Typography variant="caption" display="block" color="text.secondary">
              SUPER_ADMIN always has full access in middleware; matrix is informational.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {permsLoading ? (
            <Typography>Loading…</Typography>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Module</TableCell>
                    <TableCell align="center">C</TableCell>
                    <TableCell align="center">R</TableCell>
                    <TableCell align="center">U</TableCell>
                    <TableCell align="center">D</TableCell>
                    <TableCell>Extras</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rolePerms as any[]).map((row) => {
                    const id = row.id as string;
                    const locked = !canManage || !id;
                    return (
                      <TableRow key={id || moduleKey(row)}>
                        <TableCell>
                          <Typography fontSize="0.85rem" fontWeight={600}>
                            {moduleLabel(row) || moduleKey(row)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {moduleKey(row)}
                          </Typography>
                        </TableCell>
                        {(["canCreate", "canRead", "canUpdate", "canDelete"] as const).map(
                          (field) => (
                            <TableCell key={field} align="center">
                              <Checkbox
                                size="small"
                                checked={Boolean(row[field])}
                                disabled={locked}
                                onChange={(e) =>
                                  id &&
                                  updatePermMutation.mutate({
                                    id,
                                    body: { [field]: e.target.checked },
                                  })
                                }
                              />
                            </TableCell>
                          )
                        )}
                        <TableCell sx={{ minWidth: 200 }}>
                          {(() => {
                            const modKey = moduleKey(row);
                            const options = extrasForModule(modKey);
                            if (!options.length) {
                              return (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              );
                            }
                            return (
                              <FormControl size="small" fullWidth disabled={locked}>
                                <Select
                                  multiple
                                  displayEmpty
                                  value={(row.extraActions || []).filter((a: string) =>
                                    options.some((o) => o.value === a)
                                  )}
                                  renderValue={(selected) => {
                                    const vals = selected as string[];
                                    if (!vals.length) return "—";
                                    return vals
                                      .map(
                                        (v) =>
                                          options.find((o) => o.value === v)?.label || v
                                      )
                                      .join(", ");
                                  }}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const extras =
                                      typeof value === "string"
                                        ? value.split(",")
                                        : (value as string[]);
                                    id &&
                                      updatePermMutation.mutate({
                                        id,
                                        body: { extraActions: extras },
                                      });
                                  }}
                                >
                                  {options.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      <Checkbox
                                        size="small"
                                        checked={(row.extraActions || []).includes(
                                          opt.value
                                        )}
                                      />
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermRole(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPage;
